import AnimationsManager from 'src/sdk-components/AnimationsManager';
import { DeviceManager } from 'src/utils';
import { disposeMesh } from 'src/utils/object3DHelpers';
import { ComponentInteractionType, Inputs, LoadingState, Outputs, SceneComponent } from './types';
import getThreeX from '../ChromaKeyMaterial/THREEx';
import { DEFAULT_VALUES } from 'src/sdk-components/ChromaKeyMaterial/constants';

const DEFAULT_WIDTH = 1;

// const DEFAULT_RADIUS = 0.1;

export class VideoRenderer extends SceneComponent {
  private video: HTMLVideoElement;

  private material: any;

  private texture: any;

  private chromaTexture: any;

  private root: any = null;

  private pivot: any = null;

  private plane: any = null;

  public isLoaded = false;

  private animationsManager: AnimationsManager | null;

  private backSidePlane: any = null;

  private playBtn: any = null;

  private muteBtn: any = null;

  private aspect = 1.77;

  private aspectHeight: number = DEFAULT_WIDTH / this.aspect;

  private hasPlayPermission = false;

  public canPlay = false;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  destroyButton = () => {};

  inputs: Inputs = {
    src: null, // src must be added after the node start!
    muted: true,
    loop: true,
    autoplay: true,
    greenScreenEnabled: false,
    greenScreenColor: 0xd432,
    greenScreenStrength: 25,
    greenScreenSmoothness: 10,
    greenScreenSpill: 10,
    visible: true,
    playsInline: true,
    localRotation: { x: 0, y: 0, z: 0 },
    skipRender: false,
    enableHoverAnimation: false,
    animationPulse: false,
    animationUpDown: false,
    animationFlip: false,
    width: DEFAULT_WIDTH,
    adminPanelAccess: false,
    // useRadius: false,
    hasExternalController: false,
    isDefaultMuted: false,
    hideOnAnimationFinished: false,
  };

  outputs = {
    texture: null,
    loadingState: LoadingState.Idle,
  } as Outputs;

  // setting which events are compatible with the component
  events = {
    [ComponentInteractionType.CLICK]: true,
    [ComponentInteractionType.HOVER]: true,
    [ComponentInteractionType.DRAG]: false,
  };

  cycleVR: (time: any) => void;

  onInit() {
    const THREE = this.context.three;
    this.root = new THREE.Object3D();
    this.pivot = new THREE.Object3D();
    this.outputs.objectRoot = this.root;
    if (this.inputs.visible && !DeviceManager.isMobile()) {
      // setting the collider to add the click event
      this.outputs.collider = this.root;
    }
    this.root.add(this.pivot);
    this.animationsManager = new AnimationsManager(THREE, this.root.parent);
    const _this = this;
    this.cycleVR = function (_time: any) {
      if (_this.outputs.texture != null) {
        _this.outputs.texture.needsUpdate = true;
      }
      if (_this.chromaTexture) {
        _this.chromaTexture.value.needsUpdate = true;
      }
    };
  }

  onInputsUpdated() {
    try {
      if (this.inputs.skipRender) {
        const { localRotation } = this.inputs;
        this.root.rotation.set(localRotation.x, localRotation.y, localRotation.z);

        return;
      }

      this.releaseTexture();

      const THREE = this.context.three;
      if (!this.inputs.src) {
        //this.video.src = '';
        this.outputs.loadingState = LoadingState.Idle;
        return;
      }

      this.outputs.loadingState = LoadingState.Loading;

      if (this.inputs.src instanceof HTMLVideoElement) {
        this.video = this.inputs.src;
      } else {
        this.video = this.createVideoElement();
        this.video.preload = 'auto';
        if (typeof this.inputs.src === 'string') {
          this.video.src = this.inputs.src;
        } else {
          this.video.srcObject = this.inputs.src;
        }
        this.video.addEventListener('canplaythrough', () => {
          this.isLoaded = true;
        });
        this.video.load();
      }

      if (this.inputs.adminPanelAccess) {
        this.video.play().catch((e) => console.error(`Error play video: ${e.message}`));
      }

      const onCanPlay = async () => {
        try {
          this.canPlay = true;
          this.video.currentTime = 0.1;
          const videoElement: HTMLVideoElement & {
            webkitAudioDecodedByteCount?: number;
          } & { mozHasAudio?: boolean } & { audioTracks?: any[] } = this.video;

          const audioDataNotPresented =
            videoElement?.webkitAudioDecodedByteCount === 0 ||
            videoElement?.mozHasAudio === false ||
            videoElement?.audioTracks?.length === 0;
          if (audioDataNotPresented) {
            if (
              this.destroyButton &&
              typeof this.destroyButton === 'function' &&
              videoElement.autoplay
            ) {
              this.destroyButton();
            }
          }
          this.video.removeEventListener('canplaythrough', onCanPlay);

          const { videoWidth, videoHeight } = this.video;
          if (!videoWidth || !videoHeight) {
            // return console.debug('Unable to get video resolution.');
            throw new Error('Unable to get video resolution.');
          }

          if (
            videoWidth > this.context.renderer.capabilities.maxTextureSize ||
            videoHeight > this.context.renderer.capabilities.maxTextureSize
          ) {
            throw new Error('Unable to get process video due to video resolution.');
          }
          this.aspect = videoWidth / videoHeight;
          this.aspectHeight = this.inputs.width / this.aspect;

          const geometry = new THREE.PlaneGeometry(this.inputs.width, this.aspectHeight);

          if (this.inputs.greenScreenEnabled) {
            const THREEx = getThreeX(THREE);
            this.material = new THREEx.ChromaKeyMaterial(
              this.video,
              this.inputs.greenScreenColor || 0x04f404,
              1920,
              1080,
              this.inputs.greenScreenStrength / 100 || DEFAULT_VALUES.SIMILARITY,
              this.inputs.greenScreenSmoothness / 100 || DEFAULT_VALUES.SMOOTHNESS,
              this.inputs.greenScreenSpill / 100 || DEFAULT_VALUES.SPILL,
            );
            this.chromaTexture = this.material.uniforms.tex;
          } else {
            this.texture = new THREE.VideoTexture(this.video);
            this.texture.encoding = THREE.LinearEncoding;
            this.texture.minFilter = THREE.LinearFilter;
            this.texture.magFilter = THREE.LinearFilter;
            this.texture.format = THREE.RGBAFormat;
            this.texture.wrapS = THREE.ClampToEdgeWrapping; // THREE.RepeatWrapping
            this.texture.wrapT = THREE.ClampToEdgeWrapping; // THREE.RepeatWrapping
            this.texture.repeat.set(1, 1);

            this.material = new THREE.MeshBasicMaterial({
              map: this.texture,
              blending: THREE.NormalBlending,
              transparent: true,
              visible: this.inputs.visible,
              polygonOffset: true,
              polygonOffsetUnits: 1,
            });

            const backSideMaterial = new THREE.MeshBasicMaterial({
              side: THREE.BackSide,
              color: 0x000000,
              transparent: false,
              visible: this.inputs.visible,
              polygonOffset: true,
              polygonOffsetUnits: -2,
            });

            this.backSidePlane = new THREE.Mesh(geometry, backSideMaterial);
            this.backSidePlane.position.set(0, 0, -0.04);
            this.pivot?.add(this.backSidePlane);
          }

          this.plane = new THREE.Mesh(geometry, this.material);
          this.pivot?.add(this.plane);

          const { localRotation } = this.inputs;
          this.root.rotation.set(localRotation.x, localRotation.y, localRotation.z);

          this.playAnimation();

          this.outputs.texture = this.texture;
          this.outputs.loadingState = LoadingState.Loaded;
          this.pause();
        } catch (e) {
          console.error('[VideoRenderer]:', e);
          this.video.removeEventListener('canplaythrough', onCanPlay);
          this.outputs.loadingState = LoadingState.Error;
        }
      };
      const onError = () => {
        this.video.removeEventListener('error', onError);
        this.outputs.loadingState = LoadingState.Error;
      };

      this.video.addEventListener('canplaythrough', onCanPlay);
      this.video.addEventListener('error', onError);
      if (DeviceManager.isIOS()) {
        this.video.addEventListener(
          'pause',
          () => {
            // mute and play the video in case the browser stopped it
            // i.e. Safari stops the video if the other one was unmuted
            if (this.video.paused && this.hasPlayPermission) {
              this.toggleMute(true);
            }
          },
          false,
        );
      }
    } catch (e) {
      console.error('[VideoRenderer]:', e);
      this.outputs.loadingState = LoadingState.Error;
    }
  }

  onDestroy() {
    this.releaseTexture();
    if (this.playBtn) {
      disposeMesh(this.playBtn);
      this.playBtn = null;
    }
    if (this.muteBtn) {
      disposeMesh(this.muteBtn);
      this.muteBtn = null;
    }
  }

  isMuted() {
    return this.video.muted;
  }

  public play() {
    if (this.isLoaded)
      this.video.play().catch((e) => console.error(`Error play video: ${e.message}`));
  }

  public pause() {
    if (this.isLoaded) this.video.pause();
  }

  public load() {
    if (this.isLoaded) this.video.load();
  }

  toggleMute(muted?: boolean) {
    const value = typeof muted === 'boolean' ? muted : !this.video.muted;
    if (!this.video) return;

    if ((DeviceManager.isIOS() || DeviceManager.isSafari()) && !value && !this.hasPlayPermission) {
      // Safari support the autoplay unmute only after user interacts with Treedis website (not iframe)
      // https://developer.apple.com/documentation/webkit/delivering_video_content_for_safari#3030259
      const hasOverlay = document.getElementById('video-overlay');
      if (hasOverlay) return;
      const overlay = document.createElement('div');
      overlay.setAttribute('id', 'video-overlay');
      overlay.style.width = '100vw';
      overlay.style.height = '100vh';
      overlay.style.zIndex = '9999';
      overlay.style.position = 'fixed';
      overlay.style.top = '0';
      overlay.style.left = '0';

      const handler = async () => {
        const hasOverlay = document.getElementById('video-overlay');
        if (!hasOverlay) return;
        document.body.removeChild(overlay);
        overlay.removeEventListener('click', handler);
        // overlay.removeEventListener('mouseup', handler);
        // overlay.removeEventListener('mousemove', handler);
        // overlay.removeEventListener('touchstart', handler);
        // overlay.removeEventListener('touchmove', handler);

        this.hasPlayPermission = true;
        if (this.video) {
          await this.video.play();
          if (!this.inputs.autoplay) await this.video.pause();
        }
        // in case with iOS Safari browser, the video is muted at the moment (read the link below)
        // https://developer.apple.com/documentation/webkit/delivering_video_content_for_safari#3030259
        //
        // so, we need to unmute it and display the current state
        this.video.muted = false;
      };
      //only click event works for play permission
      overlay.addEventListener('click', handler);
      // overlay.addEventListener('mousedown', handler);
      // overlay.addEventListener('mousemove', handler);
      // overlay.addEventListener('touchstart', handler);
      // overlay.addEventListener('touchmove', handler);
      document.body.append(overlay);
    } else if (this.video) {
      // @ts-ignore
      this.video.muted = value;

      // this.video.play().catch((e) => {
      //   this.isLoaded = false;
      //   console.error(e);
      // });
      this.isLoaded = true;
    }
  }

  public playAnimation() {
    const THREE = this.context.three;

    if (!this.animationsManager) {
      this.animationsManager = new AnimationsManager(THREE, this.root.parent);
    }
    if (this.inputs.animationPulse) {
      this.animationsManager.pulse();
      this.animationsManager.play();
    } else if (this.inputs.animationUpDown) {
      this.animationsManager.bounceUpDown();
      this.animationsManager.play();
    } else if (this.inputs.animationFlip) {
      this.animationsManager.flip();
      this.animationsManager.play();
    }
  }

  toggleHoverAnimation(value: boolean) {
    if (this.animationsManager && this.inputs.enableHoverAnimation) {
      if (value) {
        if (this.animationsManager) {
          this.animationsManager.stop();
          this.animationsManager.outlinePulse();
          this.animationsManager.play();
        }
      } else {
        this.animationsManager.stop();

        if (this.inputs.animationPulse) {
          this.animationsManager.pulse();
          this.animationsManager.play();
        } else if (this.inputs.animationUpDown) {
          this.animationsManager.bounceUpDown();
          this.animationsManager.play();
        } else if (this.inputs.animationFlip) {
          this.animationsManager.flip();
          this.animationsManager.play();
        }
      }
    }
  }

  stopAnimation() {
    if (this.animationsManager) {
      this.animationsManager.stop();
      this.animationsManager = null;
    }
  }

  releaseTexture() {
    if (this.texture) {
      this.outputs.texture?.dispose();
      this.texture.dispose();
      this.outputs.loadingState = LoadingState.Idle;
    }
    if (this.video) {
      this.video.pause();
      this.video.src = '';
    }
    if (this.plane) {
      disposeMesh(this.plane);
      this.plane = null;
    }
    if (this.backSidePlane) {
      disposeMesh(this.backSidePlane);
      this.backSidePlane = null;
    }
  }

  getSize = () => [this.inputs.width, this.aspectHeight];

  private createVideoElement() {
    const { muted, loop, autoplay, playsInline } = this.inputs;
    const video: any = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.autoplay = Boolean(autoplay);
    video.muted = Boolean(muted);
    video.loop = Boolean(loop);
    video.playsInline = Boolean(playsInline);
    return video;
  }

  onTick(delta) {
    if (this.context?.renderer?.xr?.isPresenting) {
      const _session = this.context.renderer.xr.getSession();
      _session.requestAnimationFrame(this.cycleVR);
    }
    if (this.animationsManager) {
      this.animationsManager.update(delta);
    }
    if (this.inputs.adminPanelAccess) {
      this.video.play().catch(() => true);
    }
  }

  // private makeRoundedRect = (x, y, width, height, radius) => {
  //   const THREE = this.context.three;
  //
  //   const roundedRectShape = new THREE.Shape();
  //   roundedRectShape.moveTo(x, y + radius);
  //   roundedRectShape.lineTo(x, y + height - radius);
  //   roundedRectShape.quadraticCurveTo(x, y + height, x + radius, y + height);
  //   roundedRectShape.lineTo(x + width - radius, y + height);
  //   roundedRectShape.quadraticCurveTo(x + width, y + height, x + width, y + height - radius);
  //   roundedRectShape.lineTo(x + width, y + radius);
  //   roundedRectShape.quadraticCurveTo(x + width, y, x + width - radius, y);
  //   roundedRectShape.lineTo(x + radius, y);
  //   roundedRectShape.quadraticCurveTo(x, y, x, y + radius);
  //
  //   return new THREE.ShapeGeometry(roundedRectShape);
  // };
}

export const videoRendererType = 'treedis.videoRenderer';

export function makeVideoRenderer() {
  return new VideoRenderer();
}
